import { lazy, Suspense, useEffect } from "react";
import { Routes, Route, Navigate, Outlet } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { connectSse } from "@/lib/sse";
import { AppShell } from "@/components/layout/AppShell";
import { SystemShell } from "@/components/layout/SystemShell";
import { OnboardingRibbon } from "@/components/OnboardingRibbon";
import { SandboxBanner } from "@/components/SandboxBanner";
import { useAppState } from "@/lib/queries";

// Page components are lazy-loaded so each route ships its own chunk and
// the initial bundle stays small. Vite + React.lazy create one chunk per
// dynamic import; Suspense at the Routes boundary handles the loading
// state during chunk fetch.
const WorkspacePage = lazy(() => import("@/pages/operations/WorkspacePage").then((m) => ({ default: m.WorkspacePage })));
const RecordsPage = lazy(() => import("@/pages/RecordsPage").then((m) => ({ default: m.RecordsPage })));
const RecordDetailPage = lazy(() => import("@/pages/records/RecordDetailPage").then((m) => ({ default: m.RecordDetailPage })));
const ReportingPage = lazy(() => import("@/pages/reporting/ReportingPage").then((m) => ({ default: m.ReportingPage })));
const DeliveriesPage = lazy(() => import("@/pages/reporting/DeliveriesPage").then((m) => ({ default: m.DeliveriesPage })));
const RecoveryPage = lazy(() => import("@/pages/reporting/RecoveryPage").then((m) => ({ default: m.RecoveryPage })));
const CalendarPage = lazy(() => import("@/pages/CalendarPage").then((m) => ({ default: m.CalendarPage })));
const BillingPage = lazy(() => import("@/pages/BillingPage").then((m) => ({ default: m.BillingPage })));
const PlatformPage = lazy(() => import("@/pages/platform/PlatformPage").then((m) => ({ default: m.PlatformPage })));
const PlatformSetupPage = lazy(() => import("@/pages/platform/SetupPage").then((m) => ({ default: m.SetupPage })));
const ImportsPage = lazy(() => import("@/pages/platform/ImportsPage").then((m) => ({ default: m.ImportsPage })));
const PlatformAdminPage = lazy(() => import("@/pages/platform/AdminPage").then((m) => ({ default: m.AdminPage })));
const MembersPage = lazy(() => import("@/pages/platform/MembersPage").then((m) => ({ default: m.MembersPage })));
const NotificationsPage = lazy(() => import("@/pages/platform/NotificationsPage").then((m) => ({ default: m.NotificationsPage })));
const MessagingCompliancePage = lazy(() => import("@/pages/platform/MessagingCompliancePage").then((m) => ({ default: m.MessagingCompliancePage })));
const OnboardingWizard = lazy(() => import("@/pages/onboarding/OnboardingWizard").then((m) => ({ default: m.OnboardingWizard })));

const SystemOverviewPage = lazy(() => import("@/pages/system/SystemOverviewPage").then((m) => ({ default: m.SystemOverviewPage })));
const TenantsPage = lazy(() => import("@/pages/system/TenantsPage").then((m) => ({ default: m.TenantsPage })));
const PlatformAuditPage = lazy(() => import("@/pages/system/PlatformAuditPage").then((m) => ({ default: m.PlatformAuditPage })));
const QueueHealthPage = lazy(() => import("@/pages/system/QueueHealthPage").then((m) => ({ default: m.QueueHealthPage })));
const PlatformMembersPage = lazy(() => import("@/pages/system/PlatformMembersPage").then((m) => ({ default: m.PlatformMembersPage })));
const ObservabilityPage = lazy(() => import("@/pages/system/ObservabilityPage").then((m) => ({ default: m.ObservabilityPage })));
const SystemSettingsPage = lazy(() => import("@/pages/system/SystemSettingsPage").then((m) => ({ default: m.SystemSettingsPage })));
const SystemBillingPage = lazy(() => import("@/pages/system/BillingPage").then((m) => ({ default: m.BillingPage })));
const SystemSignupsPage = lazy(() => import("@/pages/system/SignupsPage").then((m) => ({ default: m.SignupsPage })));
const SystemDispatchPage = lazy(() => import("@/pages/system/DispatchPage").then((m) => ({ default: m.DispatchPage })));

const VoicemailSetupPage = lazy(() => import("@/pages/VoicemailSetupPage").then((m) => ({ default: m.VoicemailSetupPage })));
const PortalPage = lazy(() => import("@/pages/PortalPage").then((m) => ({ default: m.PortalPage })));
const LoginPage = lazy(() => import("@/pages/auth/LoginPage").then((m) => ({ default: m.LoginPage })));
const SignupPage = lazy(() => import("@/pages/auth/SignupPage").then((m) => ({ default: m.SignupPage })));
const ForgotPasswordPage = lazy(() => import("@/pages/auth/ForgotPasswordPage").then((m) => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import("@/pages/auth/ResetPasswordPage").then((m) => ({ default: m.ResetPasswordPage })));
const VerifyEmailPage = lazy(() => import("@/pages/auth/VerifyEmailPage").then((m) => ({ default: m.VerifyEmailPage })));

function RouteFallback() {
  return (
    <div className="flex h-full w-full items-center justify-center text-dw-text-3">
      <span className="text-sm">Loading…</span>
    </div>
  );
}

interface AppShape {
  uiAccess?: {
    canViewSystemConsole?: boolean;
    viewAs?: { active?: boolean };
  };
}

/**
 * Resolves the right landing surface for the current user. Platform
 * members (Dorsey-Worx employees) default to /system. Tenant users —
 * and platform members currently in view-as mode — default to
 * /operations. Wait for /api/app to load before deciding so we don't
 * flash the wrong shell.
 */
function LandingRedirect() {
  const { data: appRaw, isLoading } = useAppState();
  if (isLoading) {
    return <RouteFallback />;
  }
  const app = (appRaw ?? {}) as AppShape;
  const canSeeSystem = Boolean(app.uiAccess?.canViewSystemConsole);
  const inViewAs = Boolean(app.uiAccess?.viewAs?.active);
  if (canSeeSystem && !inViewAs) {
    return <Navigate to="/system/overview" replace />;
  }
  return <Navigate to="/operations" replace />;
}

function AuthedShell() {
  const queryClient = useQueryClient();
  useEffect(() => {
    const disconnect = connectSse(queryClient);
    return disconnect;
  }, [queryClient]);
  return (
    <AppShell>
      <SandboxBanner />
      <Suspense fallback={null}>
        <OnboardingWizard />
      </Suspense>
      <OnboardingRibbon />
      <Outlet />
    </AppShell>
  );
}

function SystemAuthedShell() {
  const queryClient = useQueryClient();
  useEffect(() => {
    const disconnect = connectSse(queryClient);
    return disconnect;
  }, [queryClient]);
  return (
    <SystemShell>
      <Outlet />
    </SystemShell>
  );
}

export function App() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Public — pre-auth pages outside the AppShell. */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/voicemail-setup" element={<VoicemailSetupPage />} />
        <Route path="/portal" element={<PortalPage />} />

        {/* Platform console — Dorsey-Worx employees only.
            Backend gates each /api/system + /api/admin endpoint. */}
        <Route element={<SystemAuthedShell />}>
          <Route path="/system" element={<Navigate to="/system/overview" replace />} />
          <Route path="/system/overview" element={<SystemOverviewPage />} />
          <Route path="/system/tenants" element={<TenantsPage />} />
          <Route path="/system/billing" element={<SystemBillingPage />} />
          <Route path="/system/signups" element={<SystemSignupsPage />} />
          <Route path="/system/dispatch" element={<SystemDispatchPage />} />
          <Route path="/system/audit" element={<PlatformAuditPage />} />
          <Route path="/system/queue" element={<QueueHealthPage />} />
          <Route path="/system/members" element={<PlatformMembersPage />} />
          <Route path="/system/observability" element={<ObservabilityPage />} />
          <Route path="/system/settings" element={<SystemSettingsPage />} />
        </Route>

        {/* Authed — operator surface inside the AppShell. lib/api.ts
            redirects to /login on 401 so unauthenticated visits to any
            authed route fall back here. */}
        <Route element={<AuthedShell />}>
          <Route path="/" element={<LandingRedirect />} />
          <Route path="/operations" element={<WorkspacePage />} />
          <Route path="/operations/*" element={<Navigate to="/operations" replace />} />
          <Route path="/records" element={<RecordsPage />} />
          <Route path="/records/:id" element={<RecordDetailPage />} />
          <Route path="/reporting" element={<ReportingPage />}>
            <Route index element={<Navigate to="recovery" replace />} />
            <Route path="recovery" element={<RecoveryPage />} />
            <Route path="deliveries" element={<DeliveriesPage />} />
            <Route path="*" element={<Navigate to="/reporting/recovery" replace />} />
          </Route>
          <Route path="/calendar" element={<CalendarPage />} />
          <Route path="/billing" element={<BillingPage />} />
          <Route path="/platform" element={<PlatformPage />}>
            <Route index element={<Navigate to="setup" replace />} />
            <Route path="setup" element={<PlatformSetupPage />} />
            <Route path="imports" element={<ImportsPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="messaging-compliance" element={<MessagingCompliancePage />} />
            <Route path="admin" element={<PlatformAdminPage />} />
            <Route path="members" element={<MembersPage />} />
          </Route>
          <Route path="/setup" element={<Navigate to="/platform/setup" replace />} />
          <Route path="/admin" element={<Navigate to="/platform/admin" replace />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
